import React, {useState, useMemo} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {Portal, Dialog, Text, Button, Checkbox, Menu, Snackbar} from 'react-native-paper';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';

const PAYMENT_MODES = [
  {label: 'Cash', value: 'CASH'},
  {label: 'UPI', value: 'UPI'},
  {label: 'Bank Transfer', value: 'BANK_TRANSFER'},
  {label: 'Cheque', value: 'CHEQUE'},
];

const itemKey = item =>
  item.instanceTaskId ? `t-${item.instanceTaskId}` : `w-${item.workAssignmentId}`;

const PayWorkerModal = ({visible, onDismiss, workerName, workerId, items, onPaymentComplete}) => {
  const [selectedKeys, setSelectedKeys] = useState(() => new Set(items.map(itemKey)));
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [menuVisible, setMenuVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  const totalDue = useMemo(() => {
    return items
      .filter(i => selectedKeys.has(itemKey(i)))
      .reduce((sum, i) => sum + (i.amount || 0), 0);
  }, [items, selectedKeys]);

  const toggleItem = key => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handlePay = async () => {
    if (selectedKeys.size === 0) {
      setSnackbar({visible: true, message: 'Select at least one item'});
      return;
    }

    const selectedItems = items.filter(i => selectedKeys.has(itemKey(i)));
    const instanceTaskIds = selectedItems.filter(i => i.instanceTaskId).map(i => i.instanceTaskId);
    const workAssignmentIds = selectedItems.filter(i => i.workAssignmentId).map(i => i.workAssignmentId);

    setLoading(true);
    try {
      await paymentApi.create({
        workerId,
        instanceTaskIds: instanceTaskIds.length > 0 ? instanceTaskIds : undefined,
        workAssignmentIds: workAssignmentIds.length > 0 ? workAssignmentIds : undefined,
        paymentMode,
      });
      onPaymentComplete?.();
      onDismiss();
    } catch (error) {
      const msg = error.response?.data?.message || 'Payment failed';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'});
  };

  const selectedModeLabel = PAYMENT_MODES.find(m => m.value === paymentMode)?.label || paymentMode;

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
        <Dialog.Title style={styles.title}>Pay Worker</Dialog.Title>
        <Dialog.Content>
          <Text style={styles.workerLabel}>Worker Name: {workerName}</Text>

          <View style={styles.tableHeader}>
            <View style={styles.checkCol} />
            <Text style={[styles.headerText, styles.typeCol]}>Type</Text>
            <Text style={[styles.headerText, styles.orderCol]}>Order#</Text>
            <Text style={[styles.headerText, styles.dateCol]}>Date</Text>
            <Text style={[styles.headerText, styles.amountCol]}>Amount</Text>
          </View>

          <ScrollView style={styles.scrollArea}>
            {items.map(item => {
              const key = itemKey(item);
              const isTask = item.type === 'Task';
              return (
                <View key={key} style={styles.row}>
                  <View style={styles.checkCol}>
                    <Checkbox
                      status={selectedKeys.has(key) ? 'checked' : 'unchecked'}
                      onPress={() => toggleItem(key)}
                      color={colors.primary}
                    />
                  </View>
                  <View style={styles.typeCol}>
                    <View style={[styles.typeBadge, {backgroundColor: isTask ? colors.primary + '25' : '#FF980025'}]}>
                      <Text style={[styles.typeBadgeText, {color: isTask ? colors.primary : '#FF9800'}]}>
                        {isTask ? 'T' : 'D'}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.cellText, styles.orderCol]} numberOfLines={1}>{item.orderNumber || '-'}</Text>
                  <Text style={[styles.cellText, styles.dateCol]}>{formatDate(item.completedAt)}</Text>
                  <Text style={[styles.cellText, styles.amountCol]}>{'\u20B9'}{item.amount || 0}</Text>
                </View>
              );
            })}
          </ScrollView>

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total Due:</Text>
            <Text style={styles.totalAmount}>{'\u20B9'}{totalDue}</Text>
          </View>

          <View style={styles.modeRow}>
            <Text style={styles.modeLabel}>Payment Mode:</Text>
            <Menu
              visible={menuVisible}
              onDismiss={() => setMenuVisible(false)}
              anchor={
                <Button
                  mode="outlined"
                  compact
                  onPress={() => setMenuVisible(true)}
                  style={styles.modeButton}>
                  {selectedModeLabel}
                </Button>
              }>
              {PAYMENT_MODES.map(mode => (
                <Menu.Item
                  key={mode.value}
                  title={mode.label}
                  onPress={() => {
                    setPaymentMode(mode.value);
                    setMenuVisible(false);
                  }}
                />
              ))}
            </Menu>
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss} textColor={colors.textSecondary}>Cancel</Button>
          <Button
            mode="contained"
            onPress={handlePay}
            loading={loading}
            disabled={loading || selectedKeys.size === 0}
            style={styles.payBtn}>
            Mark as Paid
          </Button>
        </Dialog.Actions>
      </Dialog>
      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: {maxHeight: '80%', borderRadius: 15},
  title: {fontSize: 18, fontWeight: '700'},
  workerLabel: {fontSize: 15, color: colors.text, marginBottom: spacing.md, fontWeight: '600'},
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.textSecondary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: 4,
  },
  headerText: {fontSize: 12, fontWeight: '700', color: colors.white},
  checkCol: {width: 40},
  typeCol: {width: 30},
  orderCol: {flex: 2},
  dateCol: {flex: 2},
  amountCol: {flex: 1.5, textAlign: 'right'},
  scrollArea: {maxHeight: 250},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 2,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  cellText: {fontSize: 13, color: colors.text},
  typeBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadgeText: {fontSize: 11, fontWeight: '700'},
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  totalLabel: {fontSize: 15, fontWeight: '700', color: colors.text},
  totalAmount: {fontSize: 16, fontWeight: '700', color: colors.primary},
  modeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.md,
  },
  modeLabel: {fontSize: 14, color: colors.text, marginRight: spacing.sm},
  modeButton: {borderColor: colors.border},
  payBtn: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10},
});

export default PayWorkerModal;
